/**
 * fal.ai Model → Endpoint Tables
 *
 * Single source of truth for mapping our internal model_id strings to the
 * fal.ai endpoint paths. Used by both:
 *   - server/routes/falai.ts       (interactive editor calls)
 *   - server/services/ProviderService.ts (headless API + publish view runs)
 *
 * If you add a new model: add it here, not in the consumers. Drift between
 * the two consumers caused issues like flux-2-pro returning 404 from publish
 * runs while working in the editor.
 */

import type { ModelDescriptor } from './models/types';
import { byId as catalogById, DESCRIPTOR_PRICED, skuKey } from './models/catalog';
import { selectSku } from './models/engine';

/**
 * Text-to-image endpoints. Used when no input images are provided.
 */
export const FAL_IMAGE_MODELS: Record<string, string> = {
  'z-image-turbo': 'fal-ai/z-image/turbo',
  'qwen-image': 'fal-ai/qwen-image',
  'qwen-image-edit': 'fal-ai/qwen-image-edit-2509', // Updated to use 2509 (supports multi-image)
  'seedream-4.5': 'fal-ai/bytedance/seedream/v4.5/text-to-image',
  'recraft-v3': 'fal-ai/recraft/v3/text-to-image',
  // Recraft V4 (Feb 2026) — 1MP fast tier
  'recraft-v4': 'fal-ai/recraft/v4/text-to-image',
  // Recraft V4 Pro — 4MP print-ready tier
  'recraft-v4-pro': 'fal-ai/recraft/v4/pro/text-to-image',
  // Flux 2 models (Black Forest Labs via fal.ai)
  // NOTE: 'flux-2-max' is the historical knods alias for fal-ai/flux-2-max (highest quality).
  // The new 'flux-2-pro' below is the real Black Forest Labs FLUX.2 [pro] tier
  // released alongside Max/Turbo/Flash. They are distinct endpoints.
  'flux-2-max': 'fal-ai/flux-2-max', // FLUX.2 [max] - highest quality
  'flux-2-pro': 'fal-ai/flux-2-pro', // FLUX.2 [pro] - mid-tier (BFL released alongside max)
  'flux-2-turbo': 'fal-ai/flux-2/turbo', // FLUX.2 [dev] turbo - fast, good quality
  'flux-2-flash': 'fal-ai/flux-2/flash', // FLUX.2 [dev] flash - fastest
  // Inpainting
  // v1.0-fill is HTTP 404 on fal's OpenAPI oracle and model page; the live slug is v1/fill
  // (FluxProV1FillInput, oracle 200, re-verified 2026-09-24). Billed $0.05 per megapixel of
  // OUTPUT, rounded up; the output takes the input's size — see services/fluxFillSizing.ts.
  'flux-pro-fill': 'fal-ai/flux-pro/v1/fill',
  // NOTE: Gemini image models (gemini-nano-banana, gemini-3-pro-image, gemini-3.1-flash-image)
  // route through Google AI direct via server/routes/googleai.ts — NOT through fal.ai.
  // Qwen Image 2.0 (Alibaba) — 2K native, fast
  'qwen-image-2': 'fal-ai/qwen-image-2/text-to-image',
  // Qwen Image 2.0 Pro — highest-fidelity tier, native 2048×2048
  'qwen-image-2-pro': 'fal-ai/qwen-image-2/pro/text-to-image',
  // Seedream 5.0 Lite (ByteDance) — web search + reasoning
  'seedream-5-lite': 'fal-ai/bytedance/seedream/v5/lite/text-to-image',
  // GLM-Image (Zhipu AI) — excellent text rendering
  'glm-image': 'fal-ai/glm-image',
  // Kling Image O3 (Kuaishou) — up to 4K, multi-reference editing.
  // NOTE: fal names the Kling 3.0 generation "/o3/" in the endpoint path, same
  // quirk as the Kling VIDEO entries below. The id is deliberately
  // 'kling-image-o3' so it can never collide with 'kling-3.0-standard'/'-pro',
  // which are the VIDEO keys in FAL_VIDEO_MODELS.
  'kling-image-o3': 'fal-ai/kling-image/o3/text-to-image',
};

/**
 * Image-to-image endpoints. Used when at least one input image is provided.
 * Models not listed here will fall back to FAL_IMAGE_MODELS even when given
 * input images (some models e.g. Recraft don't support image-to-image).
 */
export const FAL_IMAGE_TO_IMAGE_MODELS: Record<string, string> = {
  'z-image-turbo': 'fal-ai/z-image/turbo/image-to-image',
  'qwen-image': 'fal-ai/qwen-image-edit-2509', // Qwen uses same edit endpoint
  'qwen-image-edit': 'fal-ai/qwen-image-edit-2509',
  'seedream-4.5': 'fal-ai/bytedance/seedream/v4.5/edit',
  // Recraft doesn't support image-to-image (V3 and V4 both)
  // Flux 2 edit endpoints
  'flux-2-max': 'fal-ai/flux-2-max/edit', // FLUX.2 [max] edit
  'flux-2-pro': 'fal-ai/flux-2-pro/edit', // FLUX.2 [pro] edit - up to 9 reference images
  'flux-2-turbo': 'fal-ai/flux-2/turbo/edit', // FLUX.2 [dev] turbo edit - fast
  'flux-2-flash': 'fal-ai/flux-2/flash/edit', // FLUX.2 [dev] flash edit - fastest
  // NOTE: Gemini image models route through Google AI direct, not fal.ai (see FAL_IMAGE_MODELS above).
  // Qwen Image 2.0 (image editing)
  'qwen-image-2': 'fal-ai/qwen-image-2/edit',
  // Qwen Image 2.0 Pro (image editing)
  'qwen-image-2-pro': 'fal-ai/qwen-image-2/pro/edit',
  // Seedream 5.0 Lite (image editing)
  'seedream-5-lite': 'fal-ai/bytedance/seedream/v5/lite/edit',
  // GLM-Image doesn't support image-to-image editing
  // Kling Image O3 — multi-reference edit, up to 10 images addressed as
  // @Image1..@Image10 inside the prompt.
  'kling-image-o3': 'fal-ai/kling-image/o3/image-to-image',
};

/**
 * Resolve the right fal.ai endpoint for an image generation request, picking
 * the image-to-image variant when input images are provided and the model
 * supports it.
 */
export function resolveFalImageEndpoint(model: string, hasInputImages: boolean): string | undefined {
  if (hasInputImages && FAL_IMAGE_TO_IMAGE_MODELS[model]) {
    return FAL_IMAGE_TO_IMAGE_MODELS[model];
  }
  return FAL_IMAGE_MODELS[model];
}

/** fal names its inpainting endpoints `.../fill` or `...-fill` (fal-ai/flux-pro/v1/fill). */
const FAL_FILL_ENDPOINT = /[-/]fill$/;

/**
 * The descriptor mode of a model's BASE image endpoint (FAL_IMAGE_MODELS[model]): 'fill' for a
 * fill-only model, whose base endpoint IS the inpainting endpoint (flux-pro-fill), else 't2i'.
 * The legacy async image route posts a model with no image-to-image endpoint there, so this is
 * legacyImageMode's `baseMode` (engineBinding.ts).
 *
 * A registered descriptor owns the answer (the mode whose endpoint is the base endpoint);
 * otherwise it is read off the endpoint itself. Derived, never listed: the hard-coded set it
 * replaces was a second source of truth a new fill-only model would silently miss.
 */
export function falImageBaseMode(model: string): 't2i' | 'fill' {
  const endpoint = FAL_IMAGE_MODELS[model];
  if (!endpoint) return 't2i';
  const desc = catalogById(model);
  if (desc?.modes.fill?.endpoint === endpoint) return 'fill';
  if (desc?.modes.t2i?.endpoint === endpoint) return 't2i';
  return FAL_FILL_ENDPOINT.test(endpoint) ? 'fill' : 't2i';
}

// ---------------------------------------------------------------------------
// Kling Image O3 — non-standard input schema
// ---------------------------------------------------------------------------
// Every other fal.ai image model in this file accepts the generic input the
// callers build: { prompt, image_size, num_images, enable_safety_checker }.
// Kling does NOT: it has no image_size and no enable_safety_checker, and uses
// aspect_ratio + resolution instead. So its input must be built from scratch.
//
// This lives HERE, next to the endpoint tables, rather than in a route, because
// THREE independent call sites need it and they have drifted before:
//   - server/routes/falai.ts  POST /generate        (sync editor route)
//   - server/routes/falai.ts  POST /generate/async  (the route the editor uses)
//   - server/services/ProviderService.ts            (headless API + published runs)
// A Kling branch in only some of them is the exact shape of bug that ships.

/** fal.ai hard cap on Kling Image reference images. */
export const KLING_IMAGE_MAX_REFS = 10;

/** Kling Image model id, so call sites don't hardcode the string. */
export const KLING_IMAGE_MODEL_ID = 'kling-image-o3';

/**
 * Build the fal.ai input payload for Kling Image O3.
 *
 * Verified schema: { prompt, image_urls?, aspect_ratio, resolution,
 * output_format, num_images, result_type, series_amount?, elements?, sync_mode? }
 *
 * Deliberately NOT exposed:
 *   - num_images > 1 and result_type:'series' would return up to 9 images while
 *     creditService.reserve() is called with units=1 — i.e. N images for the
 *     price of one. Multiples go through the node's batchCount loop, which
 *     reserves per call.
 *   - `elements` (per-character face control) needs per-image UI, not a setting.
 *
 * @param aspectRatio 'auto' is a member of the image-to-image enum ONLY; the
 *                    text-to-image schema has no 'auto' and 422s on it, so it is
 *                    folded to the schema default when there are no references.
 */
export function buildKlingImageInput(opts: {
  prompt: string;
  aspectRatio?: string;
  resolution?: string;
  outputFormat?: string;
  imageUrls?: string[];
}): Record<string, any> {
  const { prompt, aspectRatio, resolution, outputFormat, imageUrls } = opts;
  const isImageToImage = Boolean(imageUrls && imageUrls.length > 0);

  const ALLOWED_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '21:9'];
  const requested = String(aspectRatio ?? '');
  const ratio = ALLOWED_RATIOS.includes(requested)
    ? requested
    : (isImageToImage ? 'auto' : '16:9');

  const res = ['1K', '2K', '4K'].includes(String(resolution)) ? String(resolution) : '1K';
  const fmt = ['png', 'jpeg', 'webp'].includes(String(outputFormat)) ? String(outputFormat) : 'png';

  const input: Record<string, any> = {
    prompt,
    aspect_ratio: ratio,
    resolution: res,
    output_format: fmt,
    num_images: 1,
    result_type: 'single',
  };

  if (isImageToImage) {
    input.image_urls = imageUrls!.slice(0, KLING_IMAGE_MAX_REFS);
  }

  return input;
}

/**
 * Kling addresses reference images positionally as @Image1..@ImageN inside the
 * prompt. A prompt that never mentions them leaves the provider to guess, which
 * is the most common way "the reference images were ignored" gets reported.
 * When images are attached and the prompt contains no @ImageN token, prefix an
 * explicit enumeration so the mapping is unambiguous.
 */
export function ensureKlingImageReferences(prompt: string, imageCount: number): string {
  if (imageCount <= 0) return prompt;
  if (/@Image\d+/i.test(prompt)) return prompt;
  const refs = Array.from({ length: imageCount }, (_, i) => `@Image${i + 1}`).join(', ');
  return `Using ${refs} as reference: ${prompt}`;
}

/**
 * Video model endpoints. text2video used when no input image, image2video
 * used when an input image is provided. supportsAudio is informational.
 */
export interface FalVideoModelEntry {
  text2video: string;
  image2video: string;
  supportsAudio: boolean;
  /**
   * Set on models that have NO text-to-video mode (lip-sync / avatar drivers).
   * `text2video` is then only a mirror of `image2video` to satisfy the type —
   * routing to a real T2V endpoint would silently generate an unrelated video,
   * discard the audio and still bill at this model's rate. Both consumers
   * (routes/falai.ts, services/ProviderService.ts) must reject the request
   * BEFORE reserving credits instead.
   */
  requiresImage?: boolean;
  /** Additionally requires a driving audio track (lip-sync / avatar). */
  requiresAudio?: boolean;
}

/**
 * fal.ai video model ids that USED to be served and are now refused outright.
 *
 * fal's VEO 3.1 (`veo-3.1`, `veo-3.1-fast` and their `-audio` credit SKUs) was
 * retired in migration 141. Since the VEO 3.1 node moved to Google direct
 * (server/routes/veo.ts, ids `veo-3.1-*-generate-preview`) in March 2026, no
 * node, dropdown or migration produced these ids; only a hand-built request to
 * /api/falai/video/generate or a hand-written headless flow could reach them.
 *
 * They are listed rather than just deleted so a caller gets an explicit
 * "retired" answer before any credit reservation, instead of a generic
 * "invalid model". DO NOT alias them onto the Google ids: the prices differ
 * (CLAUDE.md, "Aliasing a retired model"), so an alias would change what an
 * old request bills.
 */
export const RETIRED_FAL_VIDEO_MODELS: Readonly<Record<string, string>> = {
  'veo-3.1': 'VEO 3.1 via fal.ai',
  'veo-3.1-audio': 'VEO 3.1 via fal.ai',
  'veo-3.1-fast': 'VEO 3.1 Fast via fal.ai',
  'veo-3.1-fast-audio': 'VEO 3.1 Fast via fal.ai',
};

/** The refusal message for a retired fal.ai video id, or undefined if it is not retired. */
export function retiredFalVideoModelMessage(model: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(RETIRED_FAL_VIDEO_MODELS, model)) return undefined;
  return `${RETIRED_FAL_VIDEO_MODELS[model]} ("${model}") has been retired. ` +
    'Use the VEO 3.1 node, which runs on Google directly ' +
    '(veo-3.1-fast-generate-preview, veo-3.1-generate-preview or veo-3.1-lite-generate-preview).';
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy Veo NODE migration (owner decision, 2026-09-24)
// ─────────────────────────────────────────────────────────────────────────────
//
// Flows saved before March 2026 hold 'veo3FalAI' nodes whose selectedModel is a
// bare fal id ('veo-3.1-fast' / 'veo-3.1'). The node type already collapses onto
// the Google-direct VeoVideo node on load (browser LEGACY_NODE_TYPE_MAP, server
// byCamelCase alias), but the bare id then reached /api/veo and headless
// resolveVeo31Resolution, which refuse it: those nodes could not run at all.
//
// The owner chose to MIGRATE THE SAVED NODE onto the Google id and accepts the
// Google price. This is deliberately NOT an alias of the retired id: a request
// that names 'veo-3.1-fast' directly (the fal route, a hand-built headless
// ProviderService call, a freshly created API flow) is still refused by
// RETIRED_FAL_VIDEO_MODELS above. Only a saved Veo-family node is rewritten, on
// every load path (browser migrateNodeTypes / handleLoadFlow, server
// FlowExecutionService.parseFlowState, knods graphOf, publish fetchFlowState).
//
// This module ships to the browser (see the purity test), so the Veo rules are
// restated here rather than imported from veoResolution.ts. Tests pin them to
// the VeoVideo definition (src/config/nodeDefinitions.ts) and to
// resolveVeo31Resolution, so a drift fails a suite.

/** Retired bare fal id a saved node may carry → the Google-direct id it now runs as. */
export const LEGACY_VEO_NODE_MODEL_MAP: Readonly<Record<string, string>> = {
  'veo-3.1': 'veo-3.1-generate-preview',
  'veo-3.1-audio': 'veo-3.1-generate-preview',
  'veo-3.1-fast': 'veo-3.1-fast-generate-preview',
  'veo-3.1-fast-audio': 'veo-3.1-fast-generate-preview',
};

/** React Flow / registry types that resolve to the VeoVideo node. */
const VEO_FAMILY_NODE_TYPES: ReadonlySet<string> = new Set([
  'veoVideo', 'VeoVideo', 'veo3FalAI', 'veo3Video', 'veo31Video', 'videoPrompt',
]);

/** VeoVideo definition: `duration` options and `mode` / `aspectRatio` values. */
export const VEO_NODE_DURATIONS: readonly number[] = [4, 6, 8];
export const VEO_NODE_DEFAULT_DURATION = '4';
export const VEO_NODE_MODES: readonly string[] = ['text-to-video', 'image-to-video', 'first-last-frame'];
export const VEO_NODE_ASPECT_RATIOS: readonly string[] = ['16:9', '9:16'];
/** resolveVeo31Resolution tiers for the two ids the migration produces (Fast and Standard). */
const VEO_MIGRATED_TIERS: readonly string[] = ['720p', '1080p', '4k'];

function isLegacyVeoId(v: unknown): v is string {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(LEGACY_VEO_NODE_MODEL_MAP, v);
}

/** True when this node is a Veo-family node that still carries a retired bare fal id. */
export function isLegacyVeoNode(node: { type?: unknown; data?: unknown } | null | undefined): boolean {
  if (!node || typeof node.type !== 'string' || !VEO_FAMILY_NODE_TYPES.has(node.type)) return false;
  const data = (node.data || {}) as Record<string, unknown>;
  const params = (data.parameters || {}) as Record<string, unknown>;
  return isLegacyVeoId(data.selectedModel) || isLegacyVeoId(params.selectedModel);
}

/**
 * Rewrite a legacy Veo node onto the Google-direct Veo node's model and
 * settings. Returns the SAME object when there is nothing to do, a new one
 * otherwise (never mutates). The node type is left to the existing legacy-type
 * maps. Every mapping bills at most what the user picked:
 *
 *   selectedModel  'veo-3.1-fast' → 'veo-3.1-fast-generate-preview',
 *                  'veo-3.1'      → 'veo-3.1-generate-preview' (both at
 *                  data.selectedModel and data.parameters.selectedModel). When
 *                  only parameters.selectedModel is set, data.selectedModel gets
 *                  the same id, because the handlers read the top level and
 *                  would otherwise run the registry default while the badge
 *                  quoted the parameter.
 *   mode           kept if it is a VeoVideo mode; anything else is removed (the
 *                  definition default, image-to-video).
 *   duration       '8s' / 8 / '8' → '8'. Google takes 4, 6 or 8 only: another
 *                  value goes to the largest allowed length not above it (5 → 4,
 *                  7 → 6, 10 → 8), anything below 4 or unreadable → '4'.
 *                  first-last-frame → '8' (the handler forces 8 s there anyway,
 *                  so that is what it already rendered and billed).
 *   resolution     lowercased; kept if it is 720p / 1080p / 4k, else '720p'.
 *                  1080p / 4k at a length other than 8 s cannot render (Google:
 *                  8 s only) → '720p', which costs ≤ either tier (Fast 60 vs
 *                  73 / 180, Standard 241 vs 241 / 361 credits/s). Raising the
 *                  length to 8 s would bill up to 2x what was picked.
 *   aspectRatio    kept if 16:9 / 9:16, else '16:9' (no price effect).
 *   enableAudio    kept when boolean or 'true' / 'false'; a legacy
 *                  `generateAudio` is carried over when enableAudio is absent;
 *                  anything else is removed (default on). No price effect.
 */
export function migrateLegacyVeoNode<T extends { type?: unknown; data?: unknown }>(node: T): T {
  if (!isLegacyVeoNode(node)) return node;
  const data = { ...(node.data as Record<string, unknown>) };
  const params: Record<string, unknown> = { ...((data.parameters || {}) as Record<string, unknown>) };

  if (isLegacyVeoId(data.selectedModel)) {
    data.selectedModel = LEGACY_VEO_NODE_MODEL_MAP[data.selectedModel];
  } else if (!data.selectedModel && isLegacyVeoId(params.selectedModel)) {
    data.selectedModel = LEGACY_VEO_NODE_MODEL_MAP[params.selectedModel];
  }
  if (isLegacyVeoId(params.selectedModel)) {
    params.selectedModel = LEGACY_VEO_NODE_MODEL_MAP[params.selectedModel];
  }

  // mode
  if (params.mode !== undefined && !VEO_NODE_MODES.includes(String(params.mode))) delete params.mode;
  const mode = typeof params.mode === 'string' ? params.mode : undefined;

  // duration (parameters win over a top-level value, as in every reader)
  const rawDuration = params.duration ?? data.duration;
  let seconds = Number(String(rawDuration ?? '').trim().replace(/s$/i, ''));
  if (mode === 'first-last-frame') {
    seconds = 8;
  } else if (!Number.isFinite(seconds) || seconds < VEO_NODE_DURATIONS[0]) {
    seconds = Number(VEO_NODE_DEFAULT_DURATION);
  } else {
    seconds = VEO_NODE_DURATIONS.filter((d) => d <= seconds).pop() as number;
  }
  params.duration = String(seconds);

  // resolution
  const rawResolution = params.resolution ?? data.resolution;
  if (rawResolution !== undefined && rawResolution !== null && rawResolution !== '') {
    let tier = String(rawResolution).trim().toLowerCase();
    if (!VEO_MIGRATED_TIERS.includes(tier)) tier = '720p';
    if (tier !== '720p' && seconds !== 8) tier = '720p';
    params.resolution = tier;
  }

  // aspect ratio
  if (params.aspectRatio !== undefined && !VEO_NODE_ASPECT_RATIOS.includes(String(params.aspectRatio))) {
    params.aspectRatio = '16:9';
  }

  // audio
  const isBoolish = (v: unknown) => v === true || v === false || v === 'true' || v === 'false';
  if (params.enableAudio === undefined && isBoolish(params.generateAudio)) params.enableAudio = params.generateAudio;
  if (params.enableAudio !== undefined && !isBoolish(params.enableAudio)) delete params.enableAudio;

  data.parameters = params;
  return { ...node, data };
}

export const FAL_VIDEO_MODELS: Record<string, FalVideoModelEntry> = {
  'kling-2.6': {
    text2video: 'fal-ai/kling-video/v2.6/pro/text-to-video',
    image2video: 'fal-ai/kling-video/v2.6/pro/image-to-video',
    supportsAudio: true,
  },
  'kling-2.5-turbo': {
    text2video: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
    image2video: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
    supportsAudio: false,
  },
  // Kling 3.0 (Kuaishou's product name; fal.ai still uses /o3 in the endpoint path)
  'kling-3.0-standard': {
    text2video: 'fal-ai/kling-video/o3/standard/text-to-video',
    image2video: 'fal-ai/kling-video/o3/standard/image-to-video',
    supportsAudio: true,
  },
  'kling-3.0-pro': {
    text2video: 'fal-ai/kling-video/o3/pro/text-to-video',
    image2video: 'fal-ai/kling-video/o3/pro/image-to-video',
    supportsAudio: true,
  },
  // Wan 2.2 (legacy node). The whole `fal-ai/wan/v2.1/14b/*` namespace is gone —
  // the old text2video slug 404s against fal's OpenAPI oracle while the
  // image2video one still resolves, which is why only image-less saved flows
  // ever failed. `fal-ai/wan/v2.2-a14b/text-to-video` is the live sibling of the
  // image2video endpoint already in use (both verified 200).
  'wan-2.2': {
    text2video: 'fal-ai/wan/v2.2-a14b/text-to-video',
    image2video: 'fal-ai/wan/v2.2-a14b/image-to-video',
    supportsAudio: false,
  },
  'wan-2.6': {
    text2video: 'wan/v2.6/text-to-video',
    image2video: 'wan/v2.6/image-to-video',
    supportsAudio: true,
  },
  'wan-2.7': {
    text2video: 'fal-ai/wan/v2.7/text-to-video',
    image2video: 'fal-ai/wan/v2.7/image-to-video',
    supportsAudio: true,
  },
  'ltx-2': {
    text2video: 'fal-ai/ltx-2-19b/text-to-video',
    image2video: 'fal-ai/ltx-2-19b/image-to-video',
    supportsAudio: true,
  },
  'ltx-2-fast': {
    text2video: 'fal-ai/ltx-2-19b/distilled/text-to-video',
    image2video: 'fal-ai/ltx-2-19b/distilled/image-to-video',
    supportsAudio: true,
  },
  'pixverse-5.5': {
    text2video: 'fal-ai/pixverse/v5.5/text-to-video',
    image2video: 'fal-ai/pixverse/v5.5/image-to-video',
    supportsAudio: true,
  },
  'pixverse-6': {
    // PixVerse v6 — lifelike physics + striking visuals (replaces v5.5).
    // Pricing: $0.045/s @720p, $0.090/s @1080p (audio adds ~$0.015-0.025/s).
    text2video: 'fal-ai/pixverse/v6/text-to-video',
    image2video: 'fal-ai/pixverse/v6/image-to-video',
    supportsAudio: true,
  },
  'hunyuan-1.5': {
    text2video: 'fal-ai/hunyuan-video-v1.5/text-to-video',
    image2video: 'fal-ai/hunyuan-video-v1.5/image-to-video',
    supportsAudio: false,
  },
  'minimax-hailuo': {
    // Legacy Hailuo-02 standard.
    text2video: 'fal-ai/minimax/hailuo-02/standard/text-to-video',
    image2video: 'fal-ai/minimax/hailuo-02/standard/image-to-video',
    supportsAudio: false,
  },
  'hailuo-2.3': {
    // MiniMax Hailuo 2.3 (Oct 2025) — improved micro-expressions, physical actions.
    // Pricing: ~$0.047/s @ 6s, ~$0.056/s @ 10s, 768p.
    text2video: 'fal-ai/minimax/hailuo-2.3/standard/text-to-video',
    image2video: 'fal-ai/minimax/hailuo-2.3/standard/image-to-video',
    supportsAudio: false,
  },
  // Kling AI Avatar v2 Standard — $0.0562/s. Takes { image_url, audio_url,
  // prompt? } and NOTHING else; the clip length is the length of the audio,
  // there is no `duration` field in the schema.
  //
  // 404 TRAP — the path order INVERTS between v1 and v2:
  //   v1: fal-ai/kling-video/v1/standard/ai-avatar   (version, tier, ai-avatar LAST)
  //   v2: fal-ai/kling-video/ai-avatar/v2/standard   (ai-avatar FIRST, tier last)
  // Pattern-matching the v2 path off v1 produces a 404. The Pro sibling is
  // fal-ai/kling-video/ai-avatar/v2/pro at $0.115/s (not exposed).
  //
  // The previous image2video slug, 'fal-ai/kling-lipsync/v1/standard', does not
  // exist — verified 404 against fal's OpenAPI oracle
  // (https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=...). text2video
  // pointed at kling-video/v2.6/pro/text-to-video, an unrelated model: with no
  // image connected the node silently generated a plain T2V clip, threw the
  // audio away, and billed it at the avatar rate. requiresImage/requiresAudio
  // now make that path a 400 before any credits are reserved.
  'kling-avatar': {
    text2video: 'fal-ai/kling-video/ai-avatar/v2/standard',
    image2video: 'fal-ai/kling-video/ai-avatar/v2/standard',
    supportsAudio: true,
    requiresImage: true,
    requiresAudio: true,
  },
  'ltx-2.3': {
    text2video: 'fal-ai/ltx-2.3/text-to-video',
    image2video: 'fal-ai/ltx-2.3/image-to-video',
    supportsAudio: true,
  },
  'ltx-2.3-fast': {
    text2video: 'fal-ai/ltx-2.3/text-to-video/fast',
    image2video: 'fal-ai/ltx-2.3/image-to-video/fast',
    supportsAudio: true,
  },
  // ByteDance Seedance 2.5 — BARE `bytedance/` namespace. Adding the usual
  // `fal-ai/` prefix 404s (verified against fal's OpenAPI existence oracle,
  // https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=...).
  // First-last-frame is the SAME image-to-video endpoint plus an optional
  // `end_image_url` — there is no separate FFLF endpoint (also 404-verified).
  // `reference-to-video` exists but is out of scope (needs multi-reference UI).
  'seedance-2.5': {
    text2video: 'bytedance/seedance-2.5/text-to-video',
    image2video: 'bytedance/seedance-2.5/image-to-video',
    supportsAudio: true,
  },
  // Seedance 2.0, all three tiers. Same bare `bytedance/` namespace rule as 2.5.
  // These model IDs are deliberately unchanged from the retired PiAPI route so
  // saved flows carrying selectedModel 'seedance-2' / 'seedance-2-fast' keep
  // working — only the provider and endpoint changed underneath them.
  // 'seedance-2' is fal's STANDARD tier (our UI has always called it "Pro").
  'seedance-2': {
    text2video: 'bytedance/seedance-2.0/text-to-video',
    image2video: 'bytedance/seedance-2.0/image-to-video',
    supportsAudio: true,
  },
  'seedance-2-fast': {
    text2video: 'bytedance/seedance-2.0/fast/text-to-video',
    image2video: 'bytedance/seedance-2.0/fast/image-to-video',
    supportsAudio: true,
  },
  'seedance-2-mini': {
    text2video: 'bytedance/seedance-2.0/mini/text-to-video',
    image2video: 'bytedance/seedance-2.0/mini/image-to-video',
    supportsAudio: true,
  },
  'minimax-h3': {
    // MiniMax H3 (a.k.a. Hailuo 03) — a DISTINCT, newer family from the
    // hailuo-02 / hailuo-2.3 entries above. Note the BARE `minimax/` namespace:
    // there is NO `fal-ai/` prefix on H3, unlike every Hailuo endpoint.
    // Verified against fal's own OpenAPI oracle
    // (GET https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=minimax/h3/...
    //  returns 404 for a bogus id, so a 200 is real proof, not an echo).
    // First-last-frame is NOT a separate endpoint: it is image2video plus
    // `end_image_url`. Audio is native, mandatory and has no toggle parameter.
    text2video: 'minimax/h3/text-to-video',
    image2video: 'minimax/h3/image-to-video',
    supportsAudio: true,
  },
};

/**
 * MiniMax H3 `resolution` enum — the FOUR values fal accepts, spelled exactly
 * as fal spells them. Capital P on 480P/768P is not cosmetic: fal's enum is
 * case-sensitive, and fal SILENTLY DISCARDS a value it does not recognise
 * rather than erroring, so a lowercase "480p" renders at the 2K default while
 * we bill the 480P rate. The whole point of routing every H3 resolution
 * through canonicalMinimaxH3Resolution() below is that no caller can emit a
 * spelling fal will drop.
 *
 * Verified against fal's OpenAPI oracle on 2026-09-04, both endpoints:
 *   https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=minimax/h3/image-to-video
 *   https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=minimax/h3/text-to-video
 *     resolution  enum ["480P","768P","2K","4K"]  default "2K"
 *     "480P and 768P are native generation modes; 2K and 4K upscale a 768P
 *      base result."
 *
 * NOTE for whoever adds `minimax-h3-max` later: its enum is ["480P","768P"]
 * ONLY. It needs its own list here — do not reuse this one.
 */
export const MINIMAX_H3_RESOLUTIONS = ['480P', '768P', '2K', '4K'] as const;
export type MinimaxH3Resolution = typeof MINIMAX_H3_RESOLUTIONS[number];

/** fal's own default, and the node definition's default. */
export const MINIMAX_H3_DEFAULT_RESOLUTION: MinimaxH3Resolution = '2K';

/**
 * Map a caller-supplied resolution onto the canonical H3 enum, or return
 * undefined if it is not one of the four.
 *
 * Case-insensitive on purpose: '4k' and '480p' are the spellings every other
 * video node in this repo uses, and a caller who means the right tier should
 * get it rather than a 400. What is NOT accepted is a value outside the enum
 * — '720p', '1080p', '1440p' — because H3 has no such tier and mapping one
 * onto a neighbour would bill for a resolution that never renders.
 *
 * Absence is handled by the CALLER, not here: an omitted resolution means
 * MINIMAX_H3_DEFAULT_RESOLUTION, while a supplied-but-wrong one is an error.
 */
export function canonicalMinimaxH3Resolution(value: string): MinimaxH3Resolution | undefined {
  const upper = String(value).trim().toUpperCase();
  return (MINIMAX_H3_RESOLUTIONS as readonly string[]).includes(upper)
    ? (upper as MinimaxH3Resolution)
    : undefined;
}

/**
 * LTX-2.3 Fast `resolution` enum — lowercase p, unlike MiniMax H3's capital P.
 * Getting the case wrong on either model is the same silent-drop bug in
 * opposite directions, which is why neither is spelled inline anywhere.
 *
 * Verified against fal's OpenAPI oracle on 2026-09-04, both fast endpoints:
 *   https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=fal-ai/ltx-2.3/image-to-video/fast
 *   (same for .../text-to-video/fast)
 *     resolution  enum ["1080p","1440p","2160p"]  default "1080p"
 *     duration    integer, enum [6,8,10,12,14,16,18,20], default 6
 *     duration description: "Durations longer than 10 seconds (12, 14, 16, 18,
 *       20) are only supported with 25 FPS and 1080p resolution."
 */
export const LTX_23_FAST_RESOLUTIONS = ['1080p', '1440p', '2160p'] as const;
export type Ltx23FastResolution = typeof LTX_23_FAST_RESOLUTIONS[number];
export const LTX_23_FAST_DEFAULT_RESOLUTION: Ltx23FastResolution = '1080p';

/**
 * The duration the route will actually send. fal types duration as an enum of
 * even integers; the Pro tier stops at 10s, Fast goes to 20s.
 */
export function clampLtx23Duration(duration: number, isFast: boolean): number {
  const max = isFast ? 20 : 10;
  const rounded = Math.round((Number(duration) || 6) / 2) * 2; // enum is even-only
  return Math.max(6, Math.min(max, rounded));
}

/**
 * Whether a caller-supplied video duration is a usable number of seconds: a
 * finite, positive number or numeric string. `""`, `null`, `"abc"`, `0`, a
 * negative and `Infinity` are not.
 *
 * Why this exists: an unusable duration used to reach creditService.reserve()
 * RAW while the fal payload coerced it with `Number(x) || 5`. `""` billed
 * Math.ceil("") = 0 credits and `"abc"` billed NaN, while fal rendered a real
 * ~5 s clip — a free generation. Callers substitute their own default for an
 * unusable value ONCE, before the reservation, so the reserve and the payload
 * are built from the same number.
 */
export function isUsableVideoSeconds(raw: unknown): boolean {
  if (typeof raw !== 'number' && typeof raw !== 'string') return false;
  if (typeof raw === 'string' && raw.trim() === '') return false;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0;
}

/** MiniMax H3 renders a plain INTEGER duration, clamped to 5..15 s. */
export function minimaxH3Seconds(duration: unknown): number {
  return Math.min(15, Math.max(5, Math.round(Number(duration) || 5)));
}

/** Wan 2.2 has no `duration`: it renders `num_frames` (17..161) at 16 fps. */
export const WAN_22_FPS = 16;
export function wan22NumFrames(duration: unknown): number {
  return Math.min(161, Math.max(17, Math.round((Number(duration) || 5) * WAN_22_FPS)));
}

/** Seedance 2.x renders a STRING enum "4".."15": an integer clamped to 4..15. */
export function seedance2Seconds(duration: unknown): number {
  return Math.min(15, Math.max(4, Math.round(Number(duration) || 5)));
}

/** PixVerse v5.5 renders a STRING enum "5" | "8" | "10"; v6 an integer 1..15. */
export function pixverseSeconds(model: string, duration: unknown): number {
  const rounded = Math.round(Number(duration) || 5);
  if (model === 'pixverse-5.5') return rounded >= 10 ? 10 : rounded >= 8 ? 8 : 5;
  return Math.min(15, Math.max(1, rounded));
}

/** LTX-2 / LTX-2 Fast render an integer enum 6 | 8 | 10 (nearest at or above). */
export function ltx2Seconds(duration: unknown): number {
  const n = Number(duration);
  return n <= 6 ? 6 : n <= 8 ? 8 : 10;
}

/**
 * The seconds to BILL for a fal video request whose duration has already
 * passed isUsableVideoSeconds() (or been replaced by a default): the length the
 * payload actually RENDERS, for every model whose payload builder clamps or
 * quantises the length — computed by the same helper the payload uses, so the
 * two cannot drift. Every other model returns `duration` unchanged; those
 * models either send it as-is or have their own rules (kling 5/10 rejection,
 * hunyuan's fixed length in calculateCost()).
 */
export function falVideoBilledSeconds<T>(model: string, duration: T): T | number {
  if (model === 'ltx-2.3' || model === 'ltx-2.3-fast') {
    return clampLtx23Duration(Number(duration), model === 'ltx-2.3-fast');
  }
  if (model === 'minimax-h3') return minimaxH3Seconds(duration);
  if (model === 'wan-2.2') return wan22NumFrames(duration) / WAN_22_FPS;
  // Seedance 2.x and PixVerse clamp / quantise too. Billing the raw value let a
  // 1 s Seedance request pay for 1 s of a 4 s render, and a 3 s PixVerse v5.5
  // request pay for 3 s of a 5 s one. Every value the nodes offer (Seedance
  // 4..15, PixVerse 5/8) is a fixed point of these helpers, so no node-sent
  // request bills differently.
  if (model.startsWith('seedance-2')) return seedance2Seconds(duration);
  if (model === 'pixverse-5.5' || model === 'pixverse-6') return pixverseSeconds(model, duration);
  // LTX-2 (editor route only — headless ships the raw value): enum 6/8/10.
  if (model === 'ltx-2' || model === 'ltx-2-fast') return ltx2Seconds(duration);
  return duration;
}

export function canonicalLtx23FastResolution(value: string): Ltx23FastResolution | undefined {
  const lower = String(value).trim().toLowerCase();
  // '4k' is how the rest of this repo spells 2160p; accept it as an alias
  // rather than silently pricing a 4K request at 1080p.
  const normalised = lower === '4k' ? '2160p' : lower;
  return (LTX_23_FAST_RESOLUTIONS as readonly string[]).includes(normalised)
    ? (normalised as Ltx23FastResolution)
    : undefined;
}

/**
 * The resolution LTX-2.3 Fast will actually RENDER at — which is not always the
 * one the user picked.
 *
 * fal's own duration description (quoted above, and encoded in the schema, not
 * just in prose docs) restricts durations over 10 seconds to 1080p. The route
 * honours that by downgrading, so a user who picks 1440p + 12s gets a 1080p
 * clip. Billing MUST follow the downgrade: charging the 1440p rate of 56 for a
 * 1080p render is a 2x overcharge on exactly the combination the node's own
 * duration labels ("12 seconds (1080p only)") invite the user to choose.
 *
 * This is the single rule the route, the server's billing table and the
 * client's estimate all call, so the quote, the charge and the payload cannot
 * disagree about which tier applies.
 *
 * An unrecognised resolution resolves to the 1080p default — the same value the
 * route's else-branch sends — rather than throwing, because the pricing path
 * must stay total for saved flows.
 */
export function effectiveLtx23FastResolution(
  resolution: string | undefined,
  durationSeconds: number,
): Ltx23FastResolution {
  if (clampLtx23Duration(durationSeconds, true) > 10) return '1080p';
  return canonicalLtx23FastResolution(String(resolution ?? '')) ?? LTX_23_FAST_DEFAULT_RESOLUTION;
}

/**
 * The model id a fal.ai VIDEO generation must RESERVE credits under.
 *
 * Usually the model itself. The exceptions are SKU splits: a setting changes
 * what fal charges, and the `-audio` / `-t2v` id is how that setting reaches
 * pricing. This is the ONLY place the audio flag becomes a price, so a model
 * whose cost depends on audio MUST have an arm here, or both states bill one
 * number.
 *
 *   kling-2.6 / kling-3.0-standard / kling-3.0-pro  + audio -> `<id>-audio`
 *   ltx-2.3                                         + t2v   -> ltx-2.3-t2v
 *
 * (fal's VEO 3.1 — `veo-3.1` / `veo-3.1-fast` and their `-audio` SKUs — was
 * retired in migration 141. The VEO 3.1 node is Google direct.)
 *
 * Every id this can RETURN must resolve to a price — a model_registry row or a
 * FALLBACK_MODEL_COSTS entry — or `creditService.reserve()` throws
 * "Model not found" and the generation 500s. That is why each derived id is
 * also listed in DERIVED_RESERVE_IDS in server/config/reachableModels.ts, where
 * the boot validator and the CI pricing test can both see it.
 *
 * It lives here rather than inline in server/routes/falai.ts so it is testable
 * without standing up the route, and so the route and any future consumer
 * cannot drift — the same reason the endpoint tables above are here.
 */
export function falVideoCreditModelId(
  model: string,
  opts: { enableAudio?: boolean; isTextToVideo?: boolean } = {},
): string {
  // Phase 1 (plan §5): a model takes its SKU from the descriptor's `skus[].when`
  // rule — the same rule the engine uses to reserve — only once it is
  // DESCRIPTOR-PRICED (its family's price-flip PR). Catalog membership alone is
  // NOT enough: this function picks the reserved id on the LEGACY paths too
  // (falai.ts, ProviderService, FlowExecutionService), so gating on membership
  // let registering a batch change what legacy reserves while its rollout still
  // said 'legacy'. The SKU id and its price are one decision: DESCRIPTOR_PRICED
  // is the code gate CreditService.calculateCost already uses for the price, it
  // is synchronous and pure (this module ships to the browser, so it cannot read
  // the async, DB-backed, per-surface rollout), and it flips both surfaces and
  // both paths together. skuParity.test.ts proves the two rules agree for every
  // catalog model; skuRuleGate.test.ts proves registration alone changes nothing.
  const desc = catalogById(model);
  if (desc && desc.provider === 'falai' && desc.face === 'video' && isDescriptorPriced(desc)) {
    return descriptorFalVideoSku(desc, opts);
  }
  return legacyFalVideoCreditModelId(model, opts);
}

/** True when any of the model's SKUs is in the code-gated DESCRIPTOR_PRICED set. */
export function isDescriptorPriced(desc: ModelDescriptor): boolean {
  return desc.pricing.skus.some((s) => DESCRIPTOR_PRICED.has(skuKey(desc.provider, s.id)));
}

/**
 * The descriptor SKU rule in the falVideoCreditModelId() argument shape:
 * `enableAudio` and `$mode` ('t2v' when isTextToVideo, else 'i2v').
 */
export function descriptorFalVideoSku(
  desc: ModelDescriptor,
  opts: { enableAudio?: boolean; isTextToVideo?: boolean } = {},
): string {
  // The engine's own SKU rule (one implementation): first `when` that holds, '$mode' as the mode.
  const sku = selectSku(desc, { enableAudio: !!opts.enableAudio }, opts.isTextToVideo ? 't2v' : 'i2v');
  return (sku ?? desc.pricing.skus[desc.pricing.skus.length - 1]).id;
}

/** The pre-descriptor SKU rule, kept verbatim for non-catalog models and for skuParity. */
export function legacyFalVideoCreditModelId(
  model: string,
  opts: { enableAudio?: boolean; isTextToVideo?: boolean } = {},
): string {
  const { enableAudio, isTextToVideo } = opts;
  if (enableAudio) {
    if (model === 'kling-2.6') return 'kling-2.6-audio';
    if (model === 'kling-3.0-standard') return 'kling-3.0-standard-audio';
    if (model === 'kling-3.0-pro') return 'kling-3.0-pro-audio';
  }
  // fal bills LTX-2.3 Pro text-to-video at $0.08/s, 33% more than i2v's $0.06/s.
  // The Fast tier is t2v-only and needs no remap.
  if (model === 'ltx-2.3' && isTextToVideo) return 'ltx-2.3-t2v';
  return model;
}
